"use client";

import Script from "next/script";
import { cleanDomain } from "@local/utils/src/cleanDomain";
import { getTrackingCookies } from "../getTrackingCookies";
import { sanitizeForScript, toScriptLiteral } from "../sanitizeForScript";
import { STAGE } from "@local/utils/src/services";
import type { BeaconData } from "../bootloader/types";
interface Props {
  data: BeaconData;
  overrideLocale?: string;
  overrideCurrencyCode?: string;
}
export const BloomreachLoad = ({
  data,
  overrideLocale,
  overrideCurrencyCode
}: Props) => {
  const {
    cookies,
    geo,
    loc
  } = data;
  const locale = overrideLocale ?? data.locale;
  const currencyCode = overrideCurrencyCode ?? data.currencyCode;

  // These properties used to come from ip-api alone, which ran on every
  // request. It is a fallback now (see getBeaconData), so read the
  // platform geo headers first — otherwise they go null for the majority
  // of traffic. `countryName` keeps the full-name shape Bloomreach has
  // always stored ("United States"), not the ISO code.
  const geoCity = geo?.city ?? loc?.city;
  const geoCountry = geo?.countryName ?? loc?.country;
  const geoLatitude = geo?.latitude ?? loc?.lat;
  const geoLongitude = geo?.longitude ?? loc?.lon;
  const geoTimezone = geo?.timezone ?? loc?.timezone;
  const geoPostalCode = geo?.postalCode ?? loc?.zip;
  const {
    exponeaId: exponeaCookie,
    ga: gaCookie,
    gtmUpi,
    vibes
  } = getTrackingCookies();
  const BLOOMREACH_INSTANCE_ID = STAGE === "prd" ? "abd358c8-9186-11f0-98b1-5ef0ef5e1085" //baerskin_production
  : "3e9aa362-8ccf-11f0-8f65-0245abdc9d0e"; //baerskin_staging

  const gaData = cookies.ga || gaCookie;
  const google_analytics = gaData ? gaData.split(".").slice(2, 3).join() + "." + gaData.split(".").slice(3, 4).join() : "";
  const user_anonymous_id = sanitizeForScript(cookies.gtm || gtmUpi);
  const exponeaId = sanitizeForScript(cookies.exp || exponeaCookie);
  const loaded = !!data.ip;
  const today = new Date();
  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  if (loaded) {
    return <Script id="bloomreach"
    // Needs to be after as this is loaded after data
    strategy="afterInteractive" type="text/javascript" dangerouslySetInnerHTML={{
      __html: `
            !function(e,n,t,i,r,o){function s(e){if("number"!=typeof e)return e;var n=new Date;return new Date(n.getTime()+1e3*e)}var a=4e3,c="xnpe_async_hide";function p(e){return e.reduce((function(e,n){return e[n]=function(){e._.push([n.toString(),arguments])},e}),{_:[]})}function m(e,n,t){var i=t.createElement(n);i.src=e;i.crossOrigin="anonymous";var r=t.getElementsByTagName(n)[0];return r.parentNode.insertBefore(i,r),i}function u(e){return"[object Date]"===Object.prototype.toString.call(e)}o.target=o.target||"https://api.exponea.com",o.file_path=o.file_path||o.target+"/js/exponea.min.js",r[n]=p(["anonymize","initialize","identify","getSegments","update","track","trackLink","trackEnhancedEcommerce","getHtml","showHtml","showBanner","showWebLayer","ping","getAbTest","loadDependency","getRecommendation","reloadWebLayers","_preInitialize","_initializeConfig"]),r[n].notifications=p(["isAvailable","isSubscribed","subscribe","unsubscribe"]),r[n].segments=p(["subscribe"]),r[n]["snippetVersion"]="v2.7.0",function(e,n,t){e[n]["_"+t]={},e[n]["_"+t].nowFn=Date.now,e[n]["_"+t].snippetStartTime=e[n]["_"+t].nowFn()}(r,n,"performance"),function(e,n,t,i,r,o){e[r]={sdk:e[i],sdkObjectName:i,skipExperiments:!!t.new_experiments,sign:t.token+"/"+(o.exec(n.cookie)||["","new"])[1],path:t.target}}(r,e,o,n,i,RegExp("__exponea_etc__"+"=([\\w-]+)")),function(e,n,t){m(e.file_path,n,t)}(o,t,e),function(e,n,t,i,r,o,p){if(e.new_experiments){!0===e.new_experiments&&(e.new_experiments={});var l,f=e.new_experiments.hide_class||c,_=e.new_experiments.timeout||a,g=encodeURIComponent(o.location.href.split("#")[0]);e.cookies&&e.cookies.expires&&("number"==typeof e.cookies.expires||u(e.cookies.expires)?l=s(e.cookies.expires):e.cookies.expires.tracking&&("number"==typeof e.cookies.expires.tracking||u(e.cookies.expires.tracking))&&(l=s(e.cookies.expires.tracking))),l&&l<new Date&&(l=void 0);var d=e.target+"/webxp/"+n+"/"+o[t].sign+"/modifications.min.js?http-referer="+g+"&timeout="+_+"ms"+(l?"&cookie-expires="+Math.floor(l.getTime()/1e3):"");"sync"===e.new_experiments.mode&&o.localStorage.getItem("__exponea__sync_modifications__")?function(e,n,t,i,r){t[r][n]="<"+n+' src="'+e+'"></'+n+">",i.writeln(t[r][n]),i.writeln("<"+n+">!"+r+".init && document.writeln("+r+"."+n+'.replace("/'+n+'/", "/'+n+'-async/").replace("><", " async><"))</'+n+">")}(d,n,o,p,t):function(e,n,t,i,r,o,s,a){o.documentElement.classList.add(e);var c=m(t,i,o);function p(){r[a].init||m(t.replace("/"+i+"/","/"+i+"-async/"),i,o)}function u(){o.documentElement.classList.remove(e)}c.onload=p,c.onerror=p,r.setTimeout(u,n),r[s]._revealPage=u}(f,_,d,n,o,p,r,t)}}(o,t,i,0,n,r,e),function(e,n,t){var i;e[n]._initializeConfig(t),(null===(i=t.experimental)||void 0===i?void 0:i.non_personalized_weblayers)&&e[n]._preInitialize(t),e[n].start=function(i){i&&Object.keys(i).forEach((function(e){return t[e]=i[e]})),e[n].initialize(t)}}(r,n,o)}
            (document,"exponea","script","webxpClient",window,{
              target: "https://exp.baerskintactical.com",
              token: "${BLOOMREACH_INSTANCE_ID}",
              customer: {
                user_anonymous_id: "${user_anonymous_id}",
                cookie: "${exponeaId}",
                google_analytics_id: "${google_analytics}"
              },
              track: {
                auto: true,
                activity: true,
                unloads: false,
                exits: false,
                visits: true,
                google_analytics: false,
                default_properties: {
                  approx_zip_code: ${toScriptLiteral(geoPostalCode)},
                  browser_name: ${data.ua?.browser.name ? `"${data.ua?.browser.name}"` : null},
                  browser_language: "${navigator.language.split("-")[0].toLowerCase()}",
                  browser_language_and_locale: "${navigator.language.toLowerCase()}",
                  browser_type: "${data.ua?.device.type || "desktop"}",
                  checkout_language: "${locale}",
                  checkout_locale: "${locale}",
                  city: ${toScriptLiteral(geoCity)},
                  connection_type: "${data.loc?.mobile ? "cellular" : "wifi"}",
                  cookie: ${exponeaId ? `"${exponeaId}"` : null},
                  country: ${toScriptLiteral(geoCountry)},
                  currency_checkout: "${currencyCode}",
                  day_of_week_local: "${daysOfWeek[today.getDay()]}",
                  device_brand: ${data.ua?.device.vendor ? `"${data.ua?.device.vendor}"` : null},
                  device: ${data.ua?.device.model ? `"${data.ua?.device.model}"` : null},
                  facebook_click_id: ${new URLSearchParams(window.location.search).has("fbclid") ? `"${new URLSearchParams(window.location.search).get("fbclid")?.split("#")[0]}"` : null},
                  google_analytics_id: ${google_analytics ? `"${google_analytics}"` : null},
                  google_click_id: ${new URLSearchParams(window.location.search).has("gclid") ? `"${new URLSearchParams(window.location.search).get("gclid")?.split("#")[0]}"` : null},
                  hour_local: "${today.getHours()}",
                  internet_provider: ${data.loc?.isp ? `"${data.loc?.isp}"` : null},
                  ip: ${toScriptLiteral(data.ip)},
                  is_mobile_device: ${/iphone|ipod|android|ie|blackberry|fennec/.test(data.ua?.ua.toLowerCase() || navigator.userAgent.toLowerCase())},
                  latitude: ${toScriptLiteral(geoLatitude)},
                  location: "${document.location.href || window.location.href}",
                  longitude: ${toScriptLiteral(geoLongitude)},
                  os: ${data.ua?.os.name ? `"${data.ua?.os.name}"` : null},
                  page_title: "${document.title}",
                  page_version: "${window.location.pathname.includes("/lp/") ? "lp" : "store"}",
                  path: "${window.location.pathname}",
                  proxy: ${data.loc?.proxy ? true : false},
                  url_host_name: "${window.location.hostname}",
                  url_main: "${cleanDomain(window.location.hostname)}",
                  url_parameters: ${JSON.stringify(window.location.search.replace("?", "").split("&").map(param => param.replace("=", ": ")))},
                  user_is_bot: ${data.bot ? data.bot : false},
                  region: ${data.loc?.regionName ? `"${data.loc?.regionName}"` : null},
                  referrer: ${document.referrer ? `"${document.referrer}"` : null},
                  referrer_host_name: ${(() => {
        try {
          return document.referrer ? `"${new URL(document.referrer).hostname}"` : null;
        } catch {
          return null;
        }
      })()},
                  screen_size: "${window.screen.width}x${window.screen.height}",
                  temperature: ${data.weather?.temp ? `"${data.weather?.temp}"` : null},
                  timezone: ${toScriptLiteral(geoTimezone)},
                  user_agent: ${toScriptLiteral(data.ua?.ua || navigator.userAgent)},
                  user_anonymous_id: "${user_anonymous_id}",
                  utm_campaign: ${new URLSearchParams(window.location.search).has("utm_campaign") ? `"${new URLSearchParams(window.location.search).get("utm_campaign")?.split("#")[0]}"` : null},
                  utm_content: ${new URLSearchParams(window.location.search).has("utm_content") ? `"${new URLSearchParams(window.location.search).get("utm_content")?.split("#")[0]}"` : null},
                  utm_medium: ${new URLSearchParams(window.location.search).has("utm_medium") ? `"${new URLSearchParams(window.location.search).get("utm_medium")?.split("#")[0]}"` : null},
                  utm_source: ${new URLSearchParams(window.location.search).has("utm_source") ? `"${new URLSearchParams(window.location.search).get("utm_source")?.split("#")[0]}"` : null},
                  utm_term: ${new URLSearchParams(window.location.search).has("utm_term") ? `"${new URLSearchParams(window.location.search).get("utm_term")?.split("#")[0]}"` : null},
                  vibes: ${vibes ? `"${vibes}"` : null},
                  weather_description: ${data.weather?.main ? `"${data.weather?.main}"` : null},
                  weather_description_specific: ${data.weather?.description ? `"${data.weather?.description}"` : null},
                },
              },
              debug: false,
              ping: {
                enabled: true,
                interval: 30
              },
              cookies: {
                cross_subdomain: true
              },
              transfer_identity: true,
              webOptimization: {
                experiments: false,
                webLayers: true,
                tagManager: true
              },
            });
            exponea.start({
              ping: {
                activity: {
                  click: true,
                  move: false,
                  scroll: false
                }
              }
            });
            `
    }} />;
  }
  return null;
};